/* eslint-disable indent */
import LeftArrow from '../../static/images/left-arrow.png';
import moment from 'moment';
import React, { useState, useEffect } from 'react';

import './TermPicker.scss';
import { parseInt } from 'lodash';
import PropTypes from 'prop-types';

const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];

const OPERATION = {
    ADD: 'ADD',
    SUBTRACT: 'SUBTRACT',
};

const TYPE = {
    LOWER: 'LOWER',
    UPPER: 'UPPER',
};

const UPDATE_CASE = {
    YEAR: 'YEAR',
    MONTH: 'MONTH',
};

const ERROR_MESSAGE_ENUM = {
    INVALID_DATE: 'End date cannot be less than start date',
    FUTURE_DATE: 'Future dates are not allowed',
};

const TermPicker = props => {
    const { input } = props;

    const isFutureDateAllowed = props.isFutureDateAllowed
        ? props.isFutureDateAllowed
        : false;

    const [lowerYear, setLowerYear] = useState(props.lowerYear);
    const [lowerMonth, setLowerMonth] = useState(props.lowerMonth);
    const [upperYear, setUpperYear] = useState(props.upperYear);
    const [upperMonth, setUpperMonth] = useState(props.upperMonth);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [isOpen, setIsOpen] = useState(false);

    const isValidTransition = (lowerDate, upperDate) => {
        // Check for Future Date
        const currentDate = moment();
        if (
            !isFutureDateAllowed &&
            (lowerDate.isAfter(currentDate) || upperDate.isAfter(currentDate))
        ) {
            setIsError(true);
            setErrorMessage(ERROR_MESSAGE_ENUM.FUTURE_DATE);
            return false;
        }
        if (lowerDate.isAfter(upperDate)) {
            setIsError(true);
            setErrorMessage(ERROR_MESSAGE_ENUM.INVALID_DATE);
            return false;
        }

        return true;
    };

    const updateMonthSelected = (type, monthIndex) => {
        if (type === TYPE.LOWER) {
            const newLowerMonth = parseInt(monthIndex + 1);
            const newLowerYear = parseInt(lowerYear, 10);
            const newLowerDate = moment(
                `${newLowerMonth}/${newLowerYear}`,
                'MM/YYYY'
            );
            const newUpperDate = moment(
                `${upperMonth}/${upperYear}`,
                'MM/YYYY'
            );

            if (isValidTransition(newLowerDate, newUpperDate)) {
                setIsError(false);
                setLowerMonth(newLowerMonth.toString());
                return;
            }
        }
        if (type === TYPE.UPPER) {
            const newUpperMonth = parseInt(monthIndex + 1);
            const newUpperYear = parseInt(upperYear, 10);
            const newLowerDate = moment(
                `${lowerMonth}/${lowerYear}`,
                'MM/YYYY'
            );
            const newUpperDate = moment(
                `${newUpperMonth}/${newUpperYear}`,
                'MM/YYYY'
            );
            if (isValidTransition(newLowerDate, newUpperDate)) {
                setIsError(false);
                setUpperMonth(newUpperMonth.toString());
                return;
            }
        }
    };

    const handleYearChange = (type, operation) => {
        const valueToAdd = operation === OPERATION.ADD ? 1 : -1;
        if (type === TYPE.LOWER) {
            const newLowerYear = parseInt(lowerYear, 10) + valueToAdd;
            const newLowerMonth = parseInt(lowerMonth);
            const newLowerDate = moment(
                `${newLowerMonth}/${newLowerYear}`,
                'MM/YYYY'
            );
            const newUpperDate = moment(
                `${upperMonth}/${upperYear}`,
                'MM/YYYY'
            );

            if (isValidTransition(newLowerDate, newUpperDate)) {
                setIsError(false);
                setLowerYear(newLowerYear.toString());
                return;
            }
        }
        if (type === TYPE.UPPER) {
            const newUpperYear = parseInt(upperYear, 10) + valueToAdd;
            const newUpperMonth = parseInt(upperMonth);

            const newLowerDate = moment(
                `${lowerMonth}/${lowerYear}`,
                'MM/YYYY'
            );
            const newUpperDate = moment(
                `${newUpperMonth}/${newUpperYear}`,
                'MM/YYYY'
            );

            if (isValidTransition(newLowerDate, newUpperDate)) {
                setIsError(false);
                setUpperYear(newUpperYear.toString());
                return;
            }
        }
    };

    useEffect(() => {
        if (props.input.value) {
            if (props.input.value.lowerDate) {
                const [
                    inputLowerMonth,
                    inputLowerYear,
                ] = props.input.value.lowerDate.split('/');

                setLowerMonth(Number(inputLowerMonth).toString());
                setLowerYear(inputLowerYear);
            }

            if (props.input.value.upperDate) {
                const [
                    inputUpperMonth,
                    inputUpperYear,
                ] = props.input.value.upperDate.split('/');

                setUpperMonth(Number(inputUpperMonth).toString());
                setUpperYear(inputUpperYear);
            }
        }
    }, []);

    useEffect(() => {
        input.onChange({
            lowerDate:
                lowerMonth.length === 1
                    ? `0${lowerMonth}/${lowerYear}`
                    : `${lowerMonth}/${lowerYear}`,
            upperDate:
                upperMonth.length === 1
                    ? `0${upperMonth}/${upperYear}`
                    : `${upperMonth}/${upperYear}`,
        });
    }, [input, lowerMonth, lowerYear, upperMonth, upperYear]);

    return (
        <div className="term-picker-wrapper">
            <div
                className="term-picker-month-display-wrapper"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="term-picker-month-display-lowerMonth">
                    {MONTHS[parseInt(lowerMonth - 1, 10)]} {lowerYear}
                </span>
                <span className="term-picker-month-display-to"> to </span>
                <span className="term-picker-month-display-upperMonth">
                    {MONTHS[parseInt(upperMonth - 1, 10)]} {upperYear}
                </span>
            </div>
            <div
                className={`calender-popup-wrapper-${
                    isOpen ? 'picking' : 'hidden'
                }`}
            >
                <div className="selector">
                    <div className="lower-date-selector-wrapper">
                        <div className="year-container">
                            <div
                                className="arrow left-arrow"
                                onClick={() =>
                                    handleYearChange(
                                        TYPE.LOWER,
                                        OPERATION.SUBTRACT
                                    )
                                }
                            >
                                <img src={LeftArrow} alt="Back" />
                            </div>
                            <div className="selected-year">{lowerYear}</div>
                            <div
                                className="arrow right-arrow"
                                onClick={() =>
                                    handleYearChange(TYPE.LOWER, OPERATION.ADD)
                                }
                            >
                                <img src={LeftArrow} alt="Back" />
                            </div>
                        </div>
                        <div className="month-list-container">
                            {MONTHS.map((month, index) => {
                                return (
                                    <div
                                        key={index}
                                        className={`month-list-item ${
                                            lowerMonth ===
                                            (index + 1).toString()
                                                ? 'selected'
                                                : ''
                                        } 
                                    ${
                                        index < parseInt(lowerMonth, 10) - 1
                                            ? 'isCovered'
                                            : ''
                                    }
                                    `}
                                        onClick={() =>
                                            updateMonthSelected(
                                                TYPE.LOWER,
                                                index
                                            )
                                        }
                                    >
                                        {month}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className="upper-date-selector-wrapper">
                        <div className="year-container">
                            <div
                                className="arrow left-arrow"
                                onClick={() =>
                                    handleYearChange(
                                        TYPE.UPPER,
                                        OPERATION.SUBTRACT
                                    )
                                }
                            >
                                <img src={LeftArrow} alt="Back" />
                            </div>
                            <div className="selected-year">{upperYear}</div>
                            <div
                                className="arrow right-arrow"
                                onClick={() =>
                                    handleYearChange(TYPE.UPPER, OPERATION.ADD)
                                }
                            >
                                <img src={LeftArrow} alt="Back" />
                            </div>
                        </div>
                        <div className="month-list-container">
                            {MONTHS.map((month, index) => {
                                return (
                                    <div
                                        key={index}
                                        className={`month-list-item ${
                                            upperMonth ===
                                            (index + 1).toString()
                                                ? 'selected'
                                                : ''
                                        }
                                    ${
                                        index < parseInt(upperMonth, 10) - 1
                                            ? 'isCovered'
                                            : ''
                                    }
                                    `}
                                        onClick={() =>
                                            updateMonthSelected(
                                                TYPE.UPPER,
                                                index
                                            )
                                        }
                                    >
                                        {month}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
                {isError ? (
                    <div className="error-message">{errorMessage}</div>
                ) : (
                    <></>
                )}
            </div>

            <input
                type="hidden"
                value={{
                    lowerDate:
                        lowerMonth.length === 1
                            ? `0${lowerMonth}/${lowerYear}`
                            : `${lowerMonth}/${lowerYear}`,
                    upperDate:
                        upperMonth.length === 1
                            ? `0${upperMonth}/${upperYear}`
                            : `${upperMonth}/${upperYear}`,
                }}
                id="term-picker-month-input"
            />
        </div>
    );
};

TermPicker.propTypes = {
    input: PropTypes.object,
    lowerYear: PropTypes.object,
    upperYear: PropTypes.object,
    lowerMonth: PropTypes.object,
    upperMonth: PropTypes.object,
    isFutureDateAllowed: PropTypes.bool,
};

export default TermPicker;
